import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { ago, compact, inr, num } from '../lib/format.js';
import { parseNumber } from '../lib/numbers.js';
import { Alert, Card, CardSkeleton, Empty, Field, Pill, useAsync } from '../components/ui.jsx';
import ExportButton from '../components/ExportButton.jsx';

/** Buyers, with the credit position Tally reports and the limit InvIT enforces. */
export default function Customers() {
  const session = useSession();
  const entityId = session.entities[0] && session.entities[0].id;
  const [search, setSearch] = useState('');
  const list = useAsync(() => api.get(
    `/api/parties?entity_id=${entityId}${search ? `&search=${encodeURIComponent(search)}` : ''}`), [entityId, search]);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const canEdit = ['tenant_admin', 'accounts'].includes(session.user.role);

  const exposure = (p) => {
    const limit = p.credit_limit === null ? null : num(p.credit_limit);
    if (limit === null) return null;
    return Math.min(1.35, num(p.outstanding_total) / (limit || 1));
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Customers</h1>
          <div className="page-sub">
            Ledgers come from Tally; the credit limit, the terms and the hold are InvIT's, and they are checked
            before the goods leave the godown.
          </div>
        </div>
      </div>

      <div className="toolbar">
        <Field label="Search">
          <input defaultValue={search} placeholder="name or GSTIN"
            onKeyDown={(e) => e.key === 'Enter' && setSearch(e.currentTarget.value)}
            onBlur={(e) => setSearch(e.target.value)} />
        </Field>
        <ExportButton disabled={!list.data} build={async () => ({
          filename: 'customers',
          title: 'Customers',
          subtitle: 'Credit position as last read from Tally',
          sheets: [{
            name: 'Customers',
            columns: [
              { key: 'name', header: 'Customer' }, { key: 'gstin', header: 'GSTIN' },
              { key: 'place_of_supply', header: 'Place of supply' }, { key: 'contact_phone', header: 'Phone' },
              { key: 'contact_email', header: 'E-mail' },
              { key: 'outstanding_total', header: 'Outstanding', type: 'money', total: true },
              { key: 'outstanding_overdue', header: 'Overdue', type: 'money', total: true },
              { key: 'credit_limit', header: 'Credit limit', type: 'money' },
              { key: 'credit_days', header: 'Credit days', type: 'int' },
              { key: 'hold', header: 'Credit hold' },
            ],
            rows: (await api.get(`/api/parties?entity_id=${entityId}&limit=500${search ? `&search=${encodeURIComponent(search)}` : ''}`))
              .parties.map((p) => ({ ...p, hold: p.credit_hold ? 'On hold' : 'Open' })),
          }],
        })} />
        {canEdit && (
          <button className="btn secondary" onClick={() => setImporting(!importing)}>Import contacts</button>
        )}
      </div>

      {importing && (
        <ContactImport entityId={entityId} onImported={list.reload} onClose={() => setImporting(false)} />
      )}

      <Alert onRetry={() => setError(null)}>{error}</Alert>
      <Alert onRetry={list.reload}>{list.error}</Alert>
      {list.loading && !list.data && <CardSkeleton lines={5} />}

      {list.data && (
        <Card pad={list.data.parties.length === 0}>
          {list.data.parties.length === 0 ? <Empty title="No customers match">Masters arrive with the Tally sync.</Empty> : (
            <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
              <table>
                <thead><tr><th>Customer</th><th>GSTIN</th><th className="r">Outstanding</th>
                  <th className="r">Overdue</th><th className="r">Limit</th><th>Use</th><th>State</th><th /></tr></thead>
                <tbody>
                  {list.data.parties.map((p) => {
                    const used = exposure(p);
                    return (
                      <tr key={p.id} className="clickable" onClick={() => setOpen(open === p.id ? null : p.id)}>
                        <td>{p.name}<div className="sub">{p.place_of_supply ? `Place of supply ${p.place_of_supply}` : ''}</div></td>
                        <td className="mono">{p.gstin || '—'}</td>
                        <td className="r num">{inr(p.outstanding_total, 0)}</td>
                        <td className="r num" style={{ color: num(p.outstanding_overdue) > 0 ? 'var(--critical-text)' : undefined }}>
                          {inr(p.outstanding_overdue, 0)}
                        </td>
                        <td className="r num">{p.credit_limit === null ? '—' : inr(p.credit_limit, 0)}</td>
                        <td style={{ minWidth: 110 }}>
                          {used === null ? <span className="muted">no limit</span> : (
                            <div className="meter" title={`${Math.round(used * 100)}% of the limit`}>
                              <span style={{
                                width: `${Math.min(100, used * 100)}%`,
                                background: used > 1 ? 'var(--critical)' : used > 0.85 ? 'var(--warning)' : 'var(--series-3)',
                              }} />
                            </div>
                          )}
                        </td>
                        <td>{p.credit_hold
                          ? <Pill value="cancelled" label="on hold" />
                          : <Pill value="issued" label="open" />}</td>
                        <td className="r muted" aria-hidden="true">{open === p.id ? '▴' : '▾'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {open && (
        <CreditEditor partyId={open} canEdit={canEdit} onSaved={list.reload} setError={setError}
          party={list.data && list.data.parties.find((p) => p.id === open)} />
      )}
    </>
  );
}

/**
 * Phone and e-mail for many customers from a sheet: a ledger list exported from
 * Tally with its contact columns, or the client's own. Checked first; nothing is
 * written until Import.
 */
function ContactImport({ entityId, onImported, onClose }) {
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun, f = file, over = overwrite) => {
    if (!f) return;
    setError(null); setBusy(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      const res = await api.post(
        `/api/parties/contacts/import?entity_id=${entityId}&dry_run=${dryRun}&overwrite=${over}`, { file_base64: base64 });
      setResult(res);
      if (!dryRun) onImported();
    } catch (e) { setError(e.message); setResult(null); } finally { setBusy(false); }
  };

  return (
    <Card title="Import customer contacts"
      note="An Excel or CSV with a ledger name column and a mobile, phone or e-mail column — Tally's ledger list with contact details exported to Excel works as it is."
      actions={<button className="btn small ghost" onClick={onClose}>Close</button>}>
      <Alert>{error}</Alert>
      <div className="row">
        <Field label="Sheet" hint="checked first, nothing is written yet">
          <input type="file" accept=".xlsx,.csv" disabled={busy}
            onChange={(e) => { const f = e.target.files[0]; setFile(f || null); setResult(null); if (f) run(true, f); }} />
        </Field>
        <Field label="Contacts already in InvIT" hint="what the desk typed in may be newer than the sheet">
          <label className="inline">
            <input type="checkbox" checked={overwrite} disabled={busy}
              onChange={(e) => { setOverwrite(e.target.checked); if (file) run(true, file, e.target.checked); }} />
            Replace them with the sheet's
          </label>
        </Field>
      </div>

      {result && (
        <>
          <Alert kind={result.dry_run ? 'warn' : 'ok'}>
            {result.dry_run ? 'Check' : 'Imported'}: {result.matched} of {result.total} rows matched a customer;
            {' '}{result.updated} {result.dry_run ? 'would be updated' : 'updated'} ({result.phones} phone, {result.emails} e-mail)
            {result.kept ? `, ${result.kept} already had them` : ''}
            {result.no_contact ? `, ${result.no_contact} rows had no contact` : ''}.
          </Alert>
          {result.dry_run && result.updated > 0 && (
            <button className="btn" disabled={busy} onClick={() => run(false)}>
              Import {result.updated} customer{result.updated === 1 ? '' : 's'}
            </button>
          )}
          {result.unmatched_count > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary>{result.unmatched_count} {result.unmatched_count === 1 ? 'name' : 'names'} not found among the customers</summary>
              <ul className="muted">{result.unmatched.map((u) => <li key={u.row}>Row {u.row}: {u.name}</li>)}</ul>
            </details>
          )}
          {result.unreadable.length > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary>{result.unreadable.length} {result.unreadable.length === 1 ? 'row' : 'rows'} with a contact that could not be read</summary>
              <ul className="muted">{result.unreadable.map((u) => (
                <li key={u.row}>Row {u.row}: {u.name} — {[u.phone, u.email].filter(Boolean).join(' · ')}</li>))}</ul>
            </details>
          )}
        </>
      )}
    </Card>
  );
}

function CreditEditor({ partyId, party, canEdit, onSaved, setError }) {
  const credit = useAsync(() => api.get(`/api/credit-check?party_id=${partyId}&order_value=0`), [partyId]);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [contact, setContact] = useState({
    phone: (party && party.contact_phone) || '', email: (party && party.contact_email) || '',
  });

  // Where invoices, statements and payment reminders go. Blank leaves it as it is.
  const saveContact = async () => {
    setSaved(false);
    const email = contact.email.trim();
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError('That e-mail address does not look right.'); return; }
    try {
      await api.patch(`/api/parties/${partyId}`, {
        ...(contact.phone.trim() ? { contact_phone: contact.phone.trim() } : {}),
        ...(email ? { contact_email: email } : {}),
      });
      setSaved(true); onSaved();
    } catch (e) { setError(e.message); }
  };

  React.useEffect(() => {
    if (credit.data) {
      setForm({
        credit_limit: credit.data.credit_limit ?? '',
        credit_days: credit.data.credit_days ?? '',
        credit_hold: credit.data.on_hold,
      });
    }
  }, [credit.data]);

  if (credit.loading && !credit.data) return <CardSkeleton lines={2} />;
  if (!form) return null;

  const save = async () => {
    setSaved(false);
    // Blank means "no limit" / "no terms": an explicit null, never '' or 0.
    const limit = parseNumber(form.credit_limit);
    const days = parseNumber(form.credit_days);
    if (Number.isNaN(limit) || (limit !== null && limit < 0)) { setError('Credit limit must be a number, or blank for none.'); return; }
    if (Number.isNaN(days) || (days !== null && (days < 0 || !Number.isInteger(days)))) {
      setError('Credit days must be a whole number, or blank for none.'); return;
    }
    try {
      await api.patch(`/api/parties/${partyId}/credit`, {
        credit_limit: limit,
        credit_days: days,
        credit_hold: form.credit_hold,
      });
      setSaved(true); credit.reload(); onSaved();
    } catch (e) { setError(e.message); }
  };

  return (
    <Card title={`${credit.data.party_name} — credit`}
      note={`Outstanding read from Tally ${ago(credit.data.as_of)}`}
      actions={canEdit ? <button className="btn small" onClick={save}>Save</button> : null}>
      {saved && <Alert kind="ok">Saved.</Alert>}
      <div className="row">
        <Field label="Credit limit" hint="blank for none">
          <input className="num" value={form.credit_limit} disabled={!canEdit}
            onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
        </Field>
        <Field label="Credit days">
          <input className="num" value={form.credit_days} disabled={!canEdit}
            onChange={(e) => setForm({ ...form, credit_days: e.target.value })} />
        </Field>
        <Field label="Credit hold" hint="stops new orders">
          <select value={String(form.credit_hold)} disabled={!canEdit}
            onChange={(e) => setForm({ ...form, credit_hold: e.target.value === 'true' })}>
            <option value="false">Open</option>
            <option value="true">On hold</option>
          </select>
        </Field>
        <div className="field">
          <label>Position</label>
          <div className="num" style={{ paddingTop: 8 }}>
            {inr(credit.data.outstanding, 0)} out
            {credit.data.credit_limit !== null && ` of ${compact(credit.data.credit_limit)}`}
            {credit.data.overdue > 0 && (
              <span style={{ color: 'var(--critical-text)' }}> · {inr(credit.data.overdue, 0)} overdue</span>
            )}
          </div>
        </div>
      </div>
      <div className="row" style={{ marginTop: 6 }}>
        <Field label="Phone" hint="for WhatsApp, with country code">
          <input value={contact.phone} disabled={!canEdit} placeholder="+91 98300 00000"
            onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
        </Field>
        <Field label="E-mail" hint="for invoices and reminders">
          <input value={contact.email} disabled={!canEdit} placeholder="accounts@buyer.example"
            onChange={(e) => setContact({ ...contact, email: e.target.value })} />
        </Field>
        {canEdit && (
          <div className="field" style={{ alignSelf: 'end' }}>
            <button className="btn secondary" onClick={saveContact}>Save contact</button>
          </div>
        )}
      </div>
    </Card>
  );
}
